import { isSeoCheckFrame } from "./seoCheckFrame";

/**
 * Iter-185.L — Cookie consent + privacy-by-default analytics gate.
 *
 * Truthful, category-granular consent banner that:
 *   - Defaults all non-essential categories to OFF on first visit
 *     (privacy-by-default — matches the new wave of US state privacy
 *     laws coming online: CA/CCPA, CO/CPA, CT, VA, UT, OR, TX, MT, DE
 *     and counting).
 *   - Persists the user choice in localStorage for 12 months.
 *   - Exposes a single `getConsent()` getter the analytics + PostHog +
 *     GA4 layers gate themselves on.
 *   - Surfaces `gtag('consent', 'default', ...)` + `'update'` so GA4
 *     respects Google Consent Mode v2 (required for EU traffic via
 *     Cloudflare edge + recommended industry-wide).
 *   - Fires PostHog `opt_in_capturing()` / `opt_out_capturing()` on
 *     consent toggle so session recording + capture aren't running
 *     against an unconsented user.
 *
 * Categories:
 *   essential — always on (we cannot operate without it: session, CSRF,
 *               admin login, basic preferences).
 *   analytics — PostHog + GA4 event capture + GA4 page_view.
 *   marketing — reserved for future remarketing/conversion pixels.
 *               Currently no marketing scripts ship on CarSavr, but
 *               the toggle exists so when we add one we don't have to
 *               re-prompt every user.
 *
 * Storage shape (key = `carsavr-cookie-consent-v1`):
 *   {
 *     analytics: bool,
 *     marketing: bool,
 *     decided_at: ISO string,
 *     version: 1
 *   }
 *
 * Re-prompt rules: storage version bump (e.g. when we add a new
 * category) wipes the prior consent so the user picks again.
 */

const STORAGE_KEY = "carsavr-cookie-consent-v1";
const CURRENT_VERSION = 1;

const DEFAULT_DENIED = {
  analytics: false,
  marketing: false,
  decided_at: null,
  version: CURRENT_VERSION,
};

const _listeners = new Set();

function _read() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function _write(record) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Returns the current consent record. If the user hasn't made a
 * choice yet, returns the "denied" default so callers can safely
 * gate code on `getConsent().analytics === true`.
 */
export function getConsent() {
  if (isSeoCheckFrame()) return { ...DEFAULT_DENIED };
  const stored = _read();
  return stored || { ...DEFAULT_DENIED };
}

/**
 * True ONLY when the user has actively answered the banner (Accept /
 * Reject / Customize-save). False on first visit.
 */
export function hasDecided() {
  const stored = _read();
  return Boolean(stored && stored.decided_at);
}

function _gtagConsentUpdate(consent) {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
      });
    }
  } catch {
    /* swallow */
  }
}

function _posthogToggle(consent) {
  if (typeof window === "undefined") return;
  try {
    const ph = window.posthog;
    if (!ph) return;
    if (consent.analytics) {
      if (typeof ph.opt_in_capturing === "function") ph.opt_in_capturing();
    } else {
      if (typeof ph.opt_out_capturing === "function") ph.opt_out_capturing();
    }
  } catch {
    /* swallow */
  }
}

function _notify(consent) {
  for (const cb of _listeners) {
    try { cb(consent); } catch { /* swallow */ }
  }
}

/**
 * Persist a new consent choice + fan out side effects (gtag update +
 * PostHog opt_in/out + listener notify).
 *
 * Returns the persisted record so callers can chain on it.
 */
export function setConsent({ analytics = false, marketing = false } = {}) {
  const record = {
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    decided_at: new Date().toISOString(),
    version: CURRENT_VERSION,
  };
  _write(record);
  _gtagConsentUpdate(record);
  _posthogToggle(record);
  _notify(record);
  return record;
}

/** Convenience helpers for the banner buttons. */
export const acceptAll = () => setConsent({ analytics: true, marketing: true });
export const rejectAll = () => setConsent({ analytics: false, marketing: false });

/** Wipe the user's choice (so the banner re-prompts). Useful for
 *  "Cookie settings → Re-prompt" or for tests. */
export function resetConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  _notify({ ...DEFAULT_DENIED });
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 * Used by React hook + by the analytics layer.
 */
export function onConsentChange(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

/**
 * Imperative bootstrapper — call once during app init (App.js) to:
 *   1. Issue gtag('consent', 'default', ...) with denied defaults
 *      BEFORE GA4 fires anything (Consent Mode v2 contract).
 *   2. Apply persisted consent (or denied default) to PostHog +
 *      gtag immediately so analytics state matches the user's prior
 *      choice across reloads.
 *
 * Safe to call repeatedly — idempotent.
 */
let _bootstrapped = false;
export function bootstrapConsent() {
  if (_bootstrapped || typeof window === "undefined") return;
  _bootstrapped = true;
  // Set Consent Mode v2 defaults. gtag('consent','default') must run
  // BEFORE any gtag('event'). We register it here even though the
  // gtag config call already ran in index.html — additional `default`
  // entries are merged by Google.
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        wait_for_update: 500,
      });
    }
  } catch {
    /* swallow */
  }
  const consent = getConsent();
  // Apply prior consent (or denied defaults) immediately.
  _gtagConsentUpdate(consent);
  _posthogToggle(consent);
}
